import React from 'react';
interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
}
const variantStyles = {
  default: {
    bg: 'from-slate-50 to-white',
    iconBg: 'bg-gradient-to-br from-slate-100 to-slate-200',
    iconText: 'text-slate-600',
    accent: 'text-slate-800',
    ring: 'ring-slate-200/50',
    glow: 'rgba(100,116,139,0.08)',
    dot: 'bg-slate-400'
  },
  primary: {
    bg: 'from-blue-50/80 to-white',
    iconBg: 'bg-gradient-to-br from-[#2952C4] to-[#5483df]',
    iconText: 'text-white',
    accent: 'text-[#2952C4]',
    ring: 'ring-blue-200/50',
    glow: 'rgba(41,82,196,0.06)',
    dot: 'bg-[#2952C4]'
  },
  success: {
    bg: 'from-emerald-50/80 to-white',
    iconBg: 'bg-gradient-to-br from-emerald-500 to-emerald-400',
    iconText: 'text-white',
    accent: 'text-emerald-600',
    ring: 'ring-emerald-200/50',
    glow: 'rgba(5,150,105,0.06)',
    dot: 'bg-emerald-500'
  },
  warning: {
    bg: 'from-amber-50/80 to-white',
    iconBg: 'bg-gradient-to-br from-amber-500 to-amber-400',
    iconText: 'text-white',
    accent: 'text-amber-600',
    ring: 'ring-amber-200/50',
    glow: 'rgba(217,119,6,0.06)',
    dot: 'bg-amber-500'
  },
  danger: {
    bg: 'from-rose-50/80 to-white',
    iconBg: 'bg-gradient-to-br from-rose-500 to-rose-400',
    iconText: 'text-white',
    accent: 'text-rose-600',
    ring: 'ring-rose-200/50',
    glow: 'rgba(220,38,38,0.06)',
    dot: 'bg-rose-500'
  }
};
export function StatsCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  variant = 'default'
}: StatsCardProps) {
  const styles = variantStyles[variant];
  const valueStr = String(value);
  const valueSizeClass = valueStr.length > 14 ? 'text-base sm:text-lg lg:text-xl' : valueStr.length > 10 ? 'text-lg sm:text-xl lg:text-2xl' : valueStr.length > 7 ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl';
  return <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${styles.bg} ring-1 ${styles.ring} transition-all duration-300`} style={{
    boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 8px 24px ${styles.glow}`
  }} data-id="element-324" data-mp="6rsx6xlhogt8">
      {/* Decorative background circle */}
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-[0.04]" style={{
      background: variant === 'default' ? 'radial-gradient(circle, #64748b, transparent)' : variant === 'primary' ? 'radial-gradient(circle, #2952C4, transparent)' : variant === 'success' ? 'radial-gradient(circle, #059669, transparent)' : variant === 'warning' ? 'radial-gradient(circle, #d97706, transparent)' : 'radial-gradient(circle, #dc2626, transparent)'
    }} data-id="element-325" data-mp="3o4dfm5h201cs" />

      <div className="relative p-3 sm:p-5" data-id="element-326" data-mp="1by15l885tzjc">
        {/* Top row: title + icon */}
        <div className="flex items-center justify-between gap-2 mb-2 sm:mb-4" data-id="element-327" data-mp="1fyl167d34v3d">
          <div className="flex items-center gap-2 min-w-0" data-id="element-328" data-mp="1kpjm5k90qhcy">
            <div className={`w-1.5 h-1.5 rounded-full ${styles.dot} flex-shrink-0`} data-id="element-329" data-mp="1kq3616i8bftm" />
            <p className="text-[10px] sm:text-[11px] font-semibold text-slate-400 uppercase tracking-widest leading-tight truncate" data-id="element-330" data-mp="1pnuutrnkultl">
              {title}
            </p>
          </div>
          {icon && <div className={`p-1.5 sm:p-2.5 rounded-lg sm:rounded-xl ${styles.iconBg} ${styles.iconText} flex-shrink-0 shadow-lg shadow-black/5 [&>svg]:w-3.5 [&>svg]:h-3.5 sm:[&>svg]:w-5 sm:[&>svg]:h-5`} data-id="element-331" data-mp="x21qveqjp158">
              {icon}
            </div>}
        </div>

        {/* Value */}
        <p className={`${valueSizeClass} font-extrabold leading-none tracking-tight whitespace-nowrap overflow-hidden text-ellipsis ${styles.accent}`} data-id="element-332" data-mp="2idbjce82tikj">
          {value}
        </p>

        {/* Subtitle + Trend row */}
        <div className="flex items-center gap-2 mt-1 sm:mt-2" data-id="element-333" data-mp="3mc74xfe8tj48">
          {subtitle && <p className="text-[11px] sm:text-xs text-slate-400 font-medium truncate" data-id="element-334" data-mp="45srrinstd6w">
              {subtitle}
            </p>}
          {trend && <span className={`inline-flex items-center gap-0.5 text-[10px] sm:text-[11px] font-bold px-1.5 py-0.5 rounded-md ${trend.isPositive ? 'text-emerald-600 bg-emerald-500/10' : 'text-rose-600 bg-rose-500/10'}`} data-id="element-335" data-mp="e3qh69he9g13">
              {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
            </span>}
        </div>
      </div>
    </div>;
}